import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { type ReactNode, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import config from "./config";

interface Props {
  children: ReactNode;
}

const Auth0InstanceSetter = ({ children }: { children: ReactNode }) => {
  const auth0 = useAuth0();

  useEffect(() => {
    // Make auth0 instance available globally for Apollo
    // This will be use from the apollo client, which is not a react component so it can't use useAuth0
    // As the apollo client is called per request, auth0 will already be in the window object when that happens
    window.__auth0_instance__ = auth0;

    return () => {
      delete window.__auth0_instance__;
    };
  }, [auth0]);

  return <>{children}</>;
};

export const Auth0ProviderWithNavigate = ({ children }: Props) => {
  const navigate = useNavigate();

  const domain = config.auth0.domain;
  const clientId = config.auth0.clientId;
  const audience = config.auth0.audience;

  // Use custom URL scheme for mobile apps, regular URL for web
  const isMobile = Capacitor.isNativePlatform();
  const redirectUri = isMobile
    ? 'com.futyclub.app://callback'
    : `${window.location.origin}/callback`;

  const onRedirectCallback = () => {
    navigate('/callback');
  };

  // Different configuration for mobile vs web
  if (isMobile) {
    return (
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        cacheLocation="localstorage"
        useRefreshTokens={true}
        useRefreshTokensFallback={false}
        authorizationParams={{
          redirect_uri: redirectUri,
          audience: audience,
          scope: "openid profile email offline_access"
        }}
        onRedirectCallback={onRedirectCallback}
      >
        <Auth0InstanceSetter>
          {children}
        </Auth0InstanceSetter>
      </Auth0Provider>
    );
  }

  // Web configuration
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      authorizationParams={{
        redirect_uri: redirectUri,
        audience: audience,
        scope: "openid profile email offline_access"
      }}
      onRedirectCallback={onRedirectCallback}
    >
      <Auth0InstanceSetter>
        {children}
      </Auth0InstanceSetter>
    </Auth0Provider>
  );
};
